/**
 * Geocoding Service
 * Auto-geocodes load_stops that have addresses but no lat/lng
 * Persists coordinates back to Supabase
 */

import { supabase } from '../supabaseClient';
import { geocodeAddress } from './routingEngineService';

/**
 * Geocode a single load_stop and save coordinates to DB
 * Returns true if geocoded successfully
 */
export const geocodeAndSaveStop = async (stop) => {
  if (!stop.address || (stop.latitude && stop.longitude)) {
    return false; // No address or already geocoded
  }

  try {
    const result = await geocodeAddress(stop.address);
    if (!result || result.confidence < 0.7) {
      console.warn(`Low confidence geocode for: ${stop.address}`, result);
      return false;
    }

    const { error } = await supabase
      .from('load_stops')
      .update({
        latitude: result.lat,
        longitude: result.lng,
      })
      .eq('id', stop.id);

    if (error) {
      console.error('Error saving geocoded coordinates:', error);
      return false;
    }

    console.log(`Geocoded: ${stop.address} → ${result.lat}, ${result.lng}`);
    return true;
  } catch (err) {
    console.error('Geocoding failed for:', stop.address, err);
    return false;
  }
};

/**
 * Geocode all stops for a specific load
 */
export const geocodeLoadStops = async (loadId) => {
  const { data: stops, error } = await supabase
    .from('load_stops')
    .select('id, address, latitude, longitude')
    .eq('load_id', loadId);

  if (error || !stops) {
    console.error('Error fetching stops for geocoding:', error);
    return { geocoded: 0, failed: 0 };
  }

  const needsGeocoding = stops.filter(s => s.address && (!s.latitude || !s.longitude));
  
  let geocoded = 0;
  let failed = 0;

  for (const stop of needsGeocoding) {
    // Small delay between requests to avoid rate limiting
    if (geocoded > 0) {
      await new Promise(r => setTimeout(r, 200));
    }
    
    const success = await geocodeAndSaveStop(stop);
    if (success) geocoded++;
    else failed++;
  }

  console.log(`Geocoded ${geocoded}/${needsGeocoding.length} stops for load ${loadId}`);
  return { geocoded, failed, total: needsGeocoding.length };
};

/**
 * Geocode ALL load_stops in the database that are missing coordinates
 * Useful for backfilling existing data
 */
export const geocodeAllMissingStops = async (onProgress) => {
  const { data: stops, error } = await supabase
    .from('load_stops')
    .select('id, address, latitude, longitude, load_id')
    .not('address', 'is', null)
    .or('latitude.is.null,longitude.is.null')
    .limit(200); // Process in batches

  if (error || !stops || stops.length === 0) {
    return { geocoded: 0, failed: 0, total: 0 };
  }

  let geocoded = 0;
  let failed = 0;

  for (let i = 0; i < stops.length; i++) {
    const stop = stops[i];
    
    // Rate limit: 200ms between requests
    if (i > 0) await new Promise(r => setTimeout(r, 200));

    const success = await geocodeAndSaveStop(stop);
    if (success) geocoded++;
    else failed++;

    if (onProgress) {
      onProgress({ current: i + 1, total: stops.length, geocoded, failed });
    }
  }

  return { geocoded, failed, total: stops.length };
};

/**
 * Set up a real-time listener that auto-geocodes new load_stops
 */
export const startAutoGeocoding = () => {
  const channel = supabase
    .channel('load_stops_geocode')
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'load_stops',
    }, async (payload) => {
      const stop = payload.new;
      if (stop.address && (!stop.latitude || !stop.longitude)) {
        console.log('Auto-geocoding new stop:', stop.address);
        await geocodeAndSaveStop(stop);
      }
    })
    .on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'load_stops',
    }, async (payload) => {
      const stop = payload.new;
      const old = payload.old;
      // Re-geocode if address changed and no coordinates
      if (stop.address && stop.address !== old.address && (!stop.latitude || !stop.longitude)) {
        console.log('Auto-geocoding updated stop:', stop.address);
        await geocodeAndSaveStop(stop);
      }
    })
    .subscribe();

  console.log('Auto-geocoding listener started');
  return () => supabase.removeChannel(channel);
};

export default {
  geocodeAndSaveStop,
  geocodeLoadStops,
  geocodeAllMissingStops,
  startAutoGeocoding,
};
